// pages/demo.js

import React from "react";
import Head from "next/head";
import { useRouter } from "next/router";

import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { alpha } from "@mui/material/styles";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import ComputerRoundedIcon from "@mui/icons-material/ComputerRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import ScoreboardRoundedIcon from "@mui/icons-material/ScoreboardRounded";
import TvRoundedIcon from "@mui/icons-material/TvRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";

import LandingLayout from "../components/layout/LandingLayout";

const localFeatures = [
  "Запуск локального приложения на компьютере оператора",
  "Создание матча и выбор хоккейных команд",
  "Добавление составов, номеров и логотипов",
  "Управление таймером, периодами и перерывами",
  "Изменение счёта, удалений и тайм-аутов",
  "Вывод зрительского табло на второй экран",
  "Проведение овертайма и серии буллитов",
  "Сохранение матча непосредственно на компьютере",
];

const onlineFeatures = [
  "Вход в СпортПодсчет Хоккей через браузер",
  "Создание матча и выбор сохранённых команд",
  "Управление временем, счётом и периодами",
  "Добавление удалений и командных тайм-аутов",
  "Открытие зрительского табло на отдельном экране",
  "Управление хоккейным матчем с телефона",
  "Синхронное обновление данных на подключённых устройствах",
  "Хранение матчей и команд на сервере",
];

const breadcrumbStructuredData = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Главная",
      item: "https://sportpodschet.partner-progress.ru/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Демонстрация СпортПодсчет Хоккей",
      item: "https://sportpodschet.partner-progress.ru/demo",
    },
  ],
};

function DemoVideo({
  src,
  poster,
  captions,
  title,
  accent,
  isDark,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        p: {
          xs: 1,
          sm: 1.3,
          md: 1.5,
        },
        borderRadius: {
          xs: 3.5,
          md: 5,
        },
        border: `1px solid ${alpha(
          accent,
          isDark ? 0.3 : 0.18
        )}`,
        bgcolor: isDark
          ? "rgba(255,255,255,0.035)"
          : "rgba(255,255,255,0.82)",
        boxShadow: isDark
          ? "0 28px 80px rgba(0,0,0,0.32)"
          : "0 30px 80px rgba(40,75,125,0.15)",
      }}
    >
      {/* Верхняя панель */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          px: {
            xs: 1,
            md: 1.5,
          },
          py: 1.1,
        }}
      >
        {["#FF5F57", "#FEBC2E", "#28C840"].map(
          (color) => (
            <Box
              key={color}
              sx={{
                width: 10,
                height: 10,
                flex: "0 0 auto",
                borderRadius: "50%",
                bgcolor: color,
              }}
            />
          )
        )}

        <Typography
          color="text.secondary"
          sx={{
            ml: 1,
            overflow: "hidden",
            fontSize: 12,
            fontWeight: 750,
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Видео */}

      <Box
        sx={{
          position: "relative",
          width: "100%",
          overflow: "hidden",
          aspectRatio: "16 / 9",
          borderRadius: {
            xs: 2.5,
            md: 3.8,
          },
          bgcolor: "#050A12",
        }}
      >
        <Box
          component="video"
          controls
          playsInline
          preload="none"
          poster={poster}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "contain",
            bgcolor: "#050A12",
          }}
        >
          <source
            src={src}
            type="video/mp4"
          />

          {captions && (
            <track
              kind="captions"
              src={captions}
              srcLang="ru"
              label="Русские субтитры"
            />
          )}

          Ваш браузер не поддерживает воспроизведение видео.
        </Box>
      </Box>
    </Paper>
  );
}

function FeatureList({
  items,
  accent,
}) {
  return (
    <Grid
      container
      spacing={1.3}
      sx={{
        mt: 3,
      }}
    >
      {items.map((item) => (
        <Grid
          key={item}
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
          >
            <CheckCircleRoundedIcon
              sx={{
                mt: 0.2,
                flex: "0 0 auto",
                color: accent,
                fontSize: 20,
              }}
            />

            <Typography
              sx={{
                fontSize: 14.5,
                lineHeight: 1.55,
                fontWeight: 650,
              }}
            >
              {item}
            </Typography>
          </Stack>
        </Grid>
      ))}
    </Grid>
  );
}

function DemoSection({
  id,
  eyebrow,
  title,
  description,
  features,
  videoSrc,
  poster,
  captions,
  videoTitle,
  accent,
  icon: Icon,
  reverse = false,
  notice,
  primaryButton,
  primaryIcon,
  onPrimaryClick,
  secondaryButton,
  secondaryIcon,
  secondaryHref,
  isDark,
}) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        py: {
          xs: 8,
          md: 12,
        },
        scrollMarginTop: 90,
        bgcolor: reverse
          ? isDark
            ? "rgba(255,255,255,0.025)"
            : "#F6F8FC"
          : "background.default",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: 1400,
        }}
      >
        <Grid
          container
          spacing={{
            xs: 5,
            md: 7,
            lg: 9,
          }}
          alignItems="center"
        >
          {/* Текст */}

          <Grid
            size={{
              xs: 12,
              md: 5,
            }}
            sx={{
              order: {
                xs: 1,
                md: reverse ? 2 : 1,
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1.2}
              alignItems="center"
              sx={{
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 2.5,
                  color: accent,
                  bgcolor: alpha(
                    accent,
                    isDark ? 0.16 : 0.09
                  ),
                }}
              >
                <Icon />
              </Box>

              <Typography
                component="p"
                sx={{
                  color: accent,
                  fontSize: 13,
                  lineHeight: 1.4,
                  fontWeight: 850,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                }}
              >
                {eyebrow}
              </Typography>
            </Stack>

            <Typography
              component="h2"
              sx={{
                maxWidth: 620,
                fontSize: {
                  xs: "2.1rem",
                  md: "3rem",
                },
                lineHeight: 1.1,
                fontWeight: 850,
                letterSpacing: "-0.04em",
              }}
            >
              {title}
            </Typography>

            <Typography
              component="p"
              color="text.secondary"
              sx={{
                maxWidth: 650,
                mt: 2.5,
                fontSize: {
                  xs: 16,
                  md: 17,
                },
                lineHeight: 1.75,
              }}
            >
              {description}
            </Typography>

            <Typography
              component="h3"
              sx={{
                mt: 3.5,
                fontSize: 18,
                fontWeight: 850,
              }}
            >
              Что показано в видео
            </Typography>

            <FeatureList
              items={features}
              accent={accent}
            />

            {notice && (
              <Box
                sx={{
                  mt: 3,
                  px: 2.3,
                  py: 1.8,
                  borderRadius: 3,
                  border: `1px solid ${alpha(
                    accent,
                    isDark ? 0.26 : 0.16
                  )}`,
                  bgcolor: alpha(
                    accent,
                    isDark ? 0.08 : 0.04
                  ),
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.65,
                  }}
                >
                  {notice}
                </Typography>
              </Box>
            )}

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1.5}
              sx={{
                mt: 3.5,
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={primaryIcon}
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={onPrimaryClick}
                sx={{
                  minHeight: 52,
                  px: 3,
                  borderRadius: 2.5,
                  bgcolor: accent,
                  color: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 850,
                  "&:hover": {
                    bgcolor: accent,
                    filter: "brightness(0.93)",
                  },
                }}
              >
                {primaryButton}
              </Button>

              {secondaryButton && secondaryHref && (
                <Button
                  component="a"
                  href={secondaryHref}
                  variant="outlined"
                  size="large"
                  startIcon={secondaryIcon}
                  sx={{
                    minHeight: 52,
                    px: 3,
                    borderRadius: 2.5,
                    borderColor: alpha(accent, 0.55),
                    color: accent,
                    textTransform: "none",
                    fontWeight: 800,
                    "&:hover": {
                      borderColor: accent,
                      bgcolor: alpha(accent, 0.06),
                    },
                  }}
                >
                  {secondaryButton}
                </Button>
              )}
            </Stack>
          </Grid>

          {/* Видео */}

          <Grid
            size={{
              xs: 12,
              md: 7,
            }}
            sx={{
              order: {
                xs: 2,
                md: reverse ? 1 : 2,
              },
            }}
          >
            <DemoVideo
              src={videoSrc}
              poster={poster}
              captions={captions}
              title={videoTitle}
              accent={accent}
              isDark={isDark}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default function DemoPage() {
  const router = useRouter();
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  const openContacts = () => {
    router.push("/contacts");
  };

  const openOnline = () => {
    router.push("/login");
  };

  const openTelegram = () => {
    if (typeof window !== "undefined") {
      window.open(
        "https://t.me/sportpodschet",
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  return (
    <>
      <Head>
        <title>
          Демонстрация СпортПодсчет Хоккей — локальная и онлайн-версия
        </title>

        <meta
          name="description"
          content="Видео демонстрация СпортПодсчет Хоккей: локальное и онлайн-проведение матча, хоккейный таймер, счёт, удаления, табло и управление с телефона."
        />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />

        <link
          rel="canonical"
          href="https://sportpodschet.partner-progress.ru/demo"
        />

        <meta
          property="og:title"
          content="Демонстрация СпортПодсчет Хоккей"
        />

        <meta
          property="og:description"
          content="Посмотрите работу локальной и онлайн-версии СпортПодсчет Хоккей: таймер, счёт, удаления, табло и управление с телефона."
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:locale"
          content="ru_RU"
        />

        <meta
          property="og:url"
          content="https://sportpodschet.partner-progress.ru/demo"
        />

        <meta
          property="og:image"
          content="https://sportpodschet.partner-progress.ru/demo/local-poster.webp"
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              breadcrumbStructuredData
            ),
          }}
        />
      </Head>

      <Box
        component="main"
        sx={{
          overflow: "hidden",
        }}
      >
        {/* Первый экран */}

        <Box
          component="header"
          sx={{
            position: "relative",
            overflow: "hidden",
            pt: {
              xs: 8,
              md: 11,
            },
            pb: {
              xs: 8,
              md: 10,
            },

            background: isDark
              ? `
                  linear-gradient(
                    135deg,
                    rgba(8,19,36,0.98) 0%,
                    rgba(8,35,65,0.94) 50%,
                    rgba(5,58,85,0.88) 100%
                  )
                `
              : `
                  linear-gradient(
                    135deg,
                    rgba(248,251,255,0.98) 0%,
                    rgba(232,243,255,0.94) 50%,
                    rgba(221,242,248,0.90) 100%
                  )
                `,

            borderBottom: `1px solid ${alpha(
              theme.palette.text.primary,
              0.06
            )}`,

            "&::before": {
              content: '""',
              position: "absolute",
              width: {
                xs: 420,
                md: 720,
              },
              height: {
                xs: 420,
                md: 720,
              },
              top: {
                xs: -260,
                md: -410,
              },
              right: {
                xs: -230,
                md: -180,
              },
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(0,167,216,0.22) 0%, rgba(0,115,255,0.08) 48%, transparent 72%)",
              pointerEvents: "none",
            },

            "&::after": {
              content: '""',
              position: "absolute",
              width: 500,
              height: 500,
              left: -300,
              bottom: -350,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(0,115,255,0.13) 0%, transparent 70%)",
              pointerEvents: "none",
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              position: "relative",
              zIndex: 1,
              maxWidth: 1100,
              textAlign: "center",
            }}
          >
            <Typography
              component="p"
              sx={{
                color: "primary.main",
                fontSize: 13,
                lineHeight: 1.4,
                fontWeight: 850,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              Видеообзор программы
            </Typography>

            <Typography
              component="h1"
              sx={{
                maxWidth: 950,
                mx: "auto",
                mt: 1.8,
                fontSize: {
                  xs: "2.45rem",
                  sm: "3.2rem",
                  md: "4rem",
                },
                lineHeight: 1.05,
                fontWeight: 850,
                letterSpacing: "-0.05em",
              }}
            >
              Демонстрация СпортПодсчет Хоккей
            </Typography>

            <Typography
              component="p"
              color="text.secondary"
              sx={{
                maxWidth: 900,
                mx: "auto",
                mt: 2.5,
                fontSize: {
                  xs: 16.5,
                  md: 19,
                },
                lineHeight: 1.7,
              }}
            >
              Посмотрите, как проводится хоккейный матч в
              локальной и онлайн-версии: от создания команд до
              управления временем, счётом, удалениями и
              зрительским табло.
            </Typography>

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1.5}
              justifyContent="center"
              sx={{
                mt: 4,
              }}
            >
              <Button
                component="a"
                href="#local-demo"
                variant="contained"
                size="large"
                startIcon={<ComputerRoundedIcon />}
                sx={{
                  minHeight: 54,
                  px: 3.3,
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 850,
                  boxShadow:
                    "0 12px 30px rgba(0,115,255,0.22)",
                }}
              >
                Смотреть локальную версию
              </Button>

              <Button
                component="a"
                href="#online-demo"
                variant="outlined"
                size="large"
                startIcon={<LanguageRoundedIcon />}
                sx={{
                  minHeight: 54,
                  px: 3.3,
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 850,
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.035)"
                    : "rgba(255,255,255,0.55)",
                  backdropFilter: "blur(12px)",
                }}
              >
                Смотреть онлайн-версию
              </Button>
            </Stack>

            <Stack
              direction="row"
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
              gap={1}
              sx={{
                mt: 3,
              }}
            >
              {[
                "Хоккейный таймер",
                "Счёт и удаления",
                "Зрительское табло",
                "Управление с телефона",
              ].map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.055)"
                      : "rgba(255,255,255,0.58)",
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      isDark ? 0.2 : 0.12
                    )}`,
                    fontWeight: 700,
                    backdropFilter: "blur(10px)",
                  }}
                />
              ))}
            </Stack>
          </Container>
        </Box>

        {/* Локальная версия */}

        <DemoSection
          id="local-demo"
          eyebrow="Локальная версия"
          title="Проведение матча без зависимости от интернета"
          description="В демонстрации локальной версии показан полный сценарий работы оператора на компьютере хоккейной площадки: подготовка команд, создание матча, управление временем и событиями, а также вывод табло на отдельный экран."
          features={localFeatures}
          videoSrc="/demo/sportpodschet-hockey-local.mp4"
          poster="/demo/local-poster.webp"
          captions="/demo/sportpodschet-hockey-local-ru.vtt"
          videoTitle="СпортПодсчет Хоккей — локальная версия"
          accent="#0073FF"
          icon={ComputerRoundedIcon}
          notice="Локальная версия продолжает управлять матчем и зрительским табло без постоянного подключения к интернету."
          primaryButton="Обсудить локальную версию"
          primaryIcon={<WifiOffRoundedIcon />}
          onPrimaryClick={openContacts}
          secondaryButton="Перейти к онлайн-демонстрации"
          secondaryIcon={<CloudOutlinedIcon />}
          secondaryHref="#online-demo"
          isDark={isDark}
        />

        {/* Онлайн-версия */}

        <DemoSection
          id="online-demo"
          eyebrow="Онлайн-версия"
          title="Проведение матча через браузер и управление с телефона"
          description="Онлайн-версия позволяет создать и провести хоккейный матч через браузер. Панель управления можно открыть на компьютере, планшете или смартфоне, а зрительское табло — на отдельном экране площадки."
          features={onlineFeatures}
          videoSrc="/demo/sportpodschet-hockey-online.mp4"
          poster="/demo/online-poster.webp"
          captions="/demo/sportpodschet-hockey-online-ru.vtt"
          videoTitle="СпортПодсчет Хоккей — онлайн-версия"
          accent="#00A7D8"
          icon={PhoneIphoneRoundedIcon}
          reverse
          notice="Для синхронного обновления панели управления и зрительского табло требуется стабильное интернет-соединение."
          primaryButton="Открыть онлайн-версию"
          primaryIcon={<LanguageRoundedIcon />}
          onPrimaryClick={openOnline}
          secondaryButton="Вернуться к локальной версии"
          secondaryIcon={<ComputerRoundedIcon />}
          secondaryHref="#local-demo"
          isDark={isDark}
        />

        {/* Простота управления */}

        <Box
          component="section"
          sx={{
            py: {
              xs: 8,
              md: 11,
            },
            bgcolor: "background.default",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1240,
            }}
          >
            {/* Заголовок */}

            <Box
              sx={{
                maxWidth: 850,
                mx: "auto",
                textAlign: "center",
              }}
            >
              <Typography
                component="p"
                sx={{
                  color: "primary.main",
                  fontSize: 13,
                  fontWeight: 850,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                }}
              >
                Простое управление
              </Typography>

              <Typography
                component="h2"
                sx={{
                  mt: 1.5,
                  fontSize: {
                    xs: "2rem",
                    md: "3rem",
                  },
                  lineHeight: 1.1,
                  fontWeight: 850,
                  letterSpacing: "-0.04em",
                }}
              >
                Всё необходимое оператору — в одном интерфейсе
              </Typography>

              <Typography
                color="text.secondary"
                sx={{
                  maxWidth: 760,
                  mx: "auto",
                  mt: 2,
                  fontSize: {
                    xs: 16,
                    md: 17,
                  },
                  lineHeight: 1.7,
                }}
              >
                Для проведения матча не нужно переключаться между
                несколькими программами. Время, счёт, периоды, удаления и
                табло управляются из одной панели.
              </Typography>
            </Box>

            {/* Три шага */}

            <Grid
              container
              spacing={{
                xs: 2,
                md: 2.5,
              }}
              sx={{
                mt: {
                  xs: 5,
                  md: 7,
                },
              }}
            >
              {[
                {
                  number: "01",
                  icon: ComputerRoundedIcon,
                  title: "Создайте матч",
                  description:
                    "Выберите команды, составы, продолжительность периодов и другие параметры игры.",
                },
                {
                  number: "02",
                  icon: ScoreboardRoundedIcon,
                  title: "Управляйте ходом игры",
                  description:
                    "Запускайте таймер, меняйте счёт и период, добавляйте удаления и тайм-ауты.",
                },
                {
                  number: "03",
                  icon: TvRoundedIcon,
                  title: "Показывайте табло",
                  description:
                    "Информация о матче автоматически отображается на отдельном экране для зрителей.",
                },
              ].map((item, index) => {
                const Icon = item.icon;

                return (
                  <Grid
                    key={item.number}
                    size={{
                      xs: 12,
                      md: 4,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        position: "relative",
                        height: "100%",
                        overflow: "hidden",

                        p: {
                          xs: 3,
                          md: 3.5,
                        },

                        borderRadius: 4,

                        border: `1px solid ${alpha(
                          theme.palette.text.primary,
                          isDark ? 0.13 : 0.08
                        )}`,

                        bgcolor: isDark
                          ? "rgba(255,255,255,0.035)"
                          : "#FFFFFF",

                        boxShadow: isDark
                          ? "0 18px 45px rgba(0,0,0,0.15)"
                          : "0 18px 45px rgba(35,70,120,0.07)",

                        transition:
                          "transform .25s ease, box-shadow .25s ease",

                        "&:hover": {
                          transform: "translateY(-5px)",

                          boxShadow: isDark
                            ? "0 26px 60px rgba(0,0,0,0.25)"
                            : "0 26px 60px rgba(35,70,120,0.12)",
                        },
                      }}
                    >
                      {/* Номер шага */}

                      <Typography
                        aria-hidden="true"
                        sx={{
                          position: "absolute",
                          top: 18,
                          right: 22,

                          color: alpha(
                            theme.palette.primary.main,
                            isDark ? 0.16 : 0.09
                          ),

                          fontSize: {
                            xs: 52,
                            md: 64,
                          },

                          lineHeight: 1,
                          fontWeight: 900,
                          letterSpacing: "-0.06em",
                        }}
                      >
                        {item.number}
                      </Typography>

                      {/* Иконка */}

                      <Box
                        sx={{
                          position: "relative",

                          width: 54,
                          height: 54,

                          display: "grid",
                          placeItems: "center",

                          borderRadius: 2.8,

                          color: "primary.main",

                          bgcolor: alpha(
                            theme.palette.primary.main,
                            isDark ? 0.17 : 0.09
                          ),
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 29,
                          }}
                        />
                      </Box>

                      <Typography
                        component="h3"
                        sx={{
                          position: "relative",
                          mt: 2.7,

                          fontSize: {
                            xs: 20,
                            md: 22,
                          },

                          lineHeight: 1.25,
                          fontWeight: 850,
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        color="text.secondary"
                        sx={{
                          position: "relative",
                          mt: 1.2,

                          fontSize: 15.5,
                          lineHeight: 1.7,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Основной акцент */}

            <Paper
              elevation={0}
              sx={{
                mt: {
                  xs: 3,
                  md: 4,
                },

                px: {
                  xs: 2.5,
                  md: 4,
                },

                py: {
                  xs: 2.5,
                  md: 3,
                },

                borderRadius: 4,

                border: `1px solid ${alpha(
                  theme.palette.primary.main,
                  isDark ? 0.28 : 0.15
                )}`,

                bgcolor: alpha(
                  theme.palette.primary.main,
                  isDark ? 0.09 : 0.045
                ),
              }}
            >
              <Grid
                container
                spacing={{
                  xs: 2,
                  md: 3,
                }}
                alignItems="center"
              >
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography
                    sx={{
                      fontSize: {
                        xs: 18,
                        md: 21,
                      },
                      lineHeight: 1.4,
                      fontWeight: 850,
                    }}
                  >
                    Основные действия доступны сразу — без сложных меню и
                    длительного обучения оператора
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{
                      mt: 0.8,
                      lineHeight: 1.65,
                    }}
                  >
                    Крупные элементы управления, понятные названия и быстрый
                    доступ к функциям помогают сосредоточиться на матче.
                  </Typography>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Stack
                    direction="row"
                    useFlexGap
                    flexWrap="wrap"
                    justifyContent={{
                      xs: "flex-start",
                      md: "flex-end",
                    }}
                    gap={1}
                  >
                    {[
                      "Таймер",
                      "Счёт",
                      "Период",
                      "Удаления",
                      "Тайм-аут",
                      "Табло",
                    ].map((item) => (
                      <Chip
                        key={item}
                        label={item}
                        size="small"
                        sx={{
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(255,255,255,0.8)",

                          border: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.13
                          )}`,

                          fontWeight: 750,
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Container>
        </Box>

        {/* Финальный призыв */}

        <Box
          component="section"
          sx={{
            py: {
              xs: 8,
              md: 11,
            },
            bgcolor: isDark
              ? "rgba(255,255,255,0.025)"
              : "#F6F8FC",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1400,
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                px: {
                  xs: 3,
                  sm: 5,
                  md: 8,
                },
                py: {
                  xs: 5,
                  md: 7,
                },
                borderRadius: {
                  xs: 4,
                  md: 6,
                },
                color: "#FFFFFF",
                background:
                  "linear-gradient(135deg, #005BDA 0%, #0073FF 52%, #00A7D8 100%)",
                boxShadow:
                  "0 30px 80px rgba(0,115,255,0.24)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: 380,
                  height: 380,
                  right: -120,
                  top: -180,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.10)",
                  pointerEvents: "none",
                }}
              />

              <Grid
                container
                spacing={{
                  xs: 4,
                  md: 6,
                }}
                alignItems="center"
                sx={{
                  position: "relative",
                }}
              >
                <Grid
                  size={{
                    xs: 12,
                    md: 8,
                  }}
                >
                  <Typography
                    component="p"
                    sx={{
                      mb: 1.4,
                      fontSize: 13,
                      fontWeight: 850,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.8)",
                    }}
                  >
                    Персональный показ
                  </Typography>

                  <Typography
                    component="h2"
                    sx={{
                      maxWidth: 850,
                      fontSize: {
                        xs: "2.1rem",
                        md: "3.2rem",
                      },
                      lineHeight: 1.08,
                      fontWeight: 850,
                      letterSpacing: "-0.04em",
                    }}
                  >
                    Посмотрите СпортПодсчет Хоккей на своём сценарии
                    матча
                  </Typography>

                  <Typography
                    sx={{
                      maxWidth: 820,
                      mt: 2,
                      fontSize: {
                        xs: 16,
                        md: 18,
                      },
                      lineHeight: 1.7,
                      color: "rgba(255,255,255,0.86)",
                    }}
                  >
                    Проведём персональную демонстрацию под условия
                    вашей хоккейной площадки. Покажем таймер,
                    составы, удаления, зрительское табло и
                    управление матчем с телефона.
                  </Typography>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Stack
                    spacing={1.5}
                    alignItems={{
                      xs: "stretch",
                      md: "flex-end",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={
                        <PlayCircleOutlineRoundedIcon />
                      }
                      endIcon={
                        <ArrowForwardRoundedIcon />
                      }
                      onClick={openTelegram}
                      sx={{
                        minHeight: 54,
                        px: 3.4,
                        borderRadius: 2.5,
                        bgcolor: "#FFFFFF",
                        color: "#005BDA",
                        textTransform: "none",
                        fontSize: 16,
                        fontWeight: 850,
                        "&:hover": {
                          bgcolor:
                            "rgba(255,255,255,0.92)",
                        },
                      }}
                    >
                      Записаться в Telegram
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<ScoreboardRoundedIcon />}
                      onClick={openContacts}
                      sx={{
                        minHeight: 52,
                        px: 3.3,
                        borderRadius: 2.5,
                        borderColor:
                          "rgba(255,255,255,0.55)",
                        color: "#FFFFFF",
                        textTransform: "none",
                        fontWeight: 800,
                        "&:hover": {
                          borderColor: "#FFFFFF",
                          bgcolor:
                            "rgba(255,255,255,0.08)",
                        },
                      }}
                    >
                      Задать вопрос
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
}

DemoPage.getLayout = (page) => (
  <LandingLayout>{page}</LandingLayout>
);